import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { PageWrapper, Card, Button } from "../components";
import { 
  ArrowLeft, Mic, Square, Play, Pause, RotateCcw, 
  Check, Upload, Loader2, AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RecordAffirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preloadedText = searchParams.get("text") || "";
  
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  
  const [step, setStep] = useState(1); // 1: Enter text, 2: Record, 3: Review, 4: Save
  const [affirmationText, setAffirmationText] = useState(preloadedText);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const getToken = () => localStorage.getItem("reclaim_token");

  // Recording timer
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (recordedUrl) {
        URL.revokeObjectURL(recordedUrl);
      }
    };
  }, [recordedUrl]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    try {
      console.log("[RecordAffirmation] Requesting microphone...");
      
      // Check browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Your browser doesn't support audio recording.");
        toast.error("Recording not supported in this browser");
        return;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      console.log("[RecordAffirmation] Got stream");
      
      // Determine supported mimeType
      let mimeType = 'audio/webm';
      if (typeof MediaRecorder !== 'undefined') {
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else {
          mimeType = ''; // Let browser decide
        }
      }
      
      console.log("[RecordAffirmation] Using mimeType:", mimeType);
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          console.log("[RecordAffirmation] Chunk:", e.data.size, "bytes");
        }
      };
      
      mediaRecorder.onstop = () => {
        console.log("[RecordAffirmation] Stopped, processing...");
        const actualMimeType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        console.log("[RecordAffirmation] Blob created:", blob.size, "bytes");
        setRecordedBlob(blob);
        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      toast.success("Recording started!");
      console.log("[RecordAffirmation] Recording started");
      
    } catch (err) {
      console.error("[RecordAffirmation] Error:", err);
      let errMsg;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errMsg = "Microphone access was blocked. Please tap the lock icon in your browser's address bar and allow microphone access.";
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errMsg = "Unable to access microphone. Please check your browser settings allow microphone access for this site.";
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errMsg = "Microphone is being used by another app. Please close other apps and try again.";
      } else {
        errMsg = "Could not access microphone. Please check your browser permissions and try again.";
      }
      setError(errMsg);
      toast.error(errMsg);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setStep(3); // Move to review step
    }
  };

  const reRecord = () => {
    if (recordedUrl) {
      URL.revokeObjectURL(recordedUrl);
    }
    setRecordedBlob(null);
    setRecordedUrl(null);
    setRecordingTime(0);
    setStep(2);
  };

  const togglePlayback = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const saveRecording = async () => {
    if (!recordedBlob || !affirmationText.trim()) {
      toast.error("Please record your affirmation first");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const authToken = getToken();
      
      // Create FormData with the recording
      const formData = new FormData();
      formData.append("audio", recordedBlob, "affirmation.webm");
      formData.append("affirmation_text", affirmationText.trim());
      formData.append("voice_style", "recorded"); // Mark as user-recorded
      formData.append("is_recorded", "true");

      const response = await fetch(`${API_URL}/api/voice-studio/upload-recording`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to save recording");
      }

      const data = await response.json();
      
      toast.success("Your recording has been saved!");
      setStep(4);
      
      // Navigate to installations after a short delay
      setTimeout(() => {
        navigate("/voice-studio?tab=installations");
      }, 2000);

    } catch (err) {
      console.error("Save error:", err);
      setError(err.message);
      toast.error(err.message || "Failed to save recording");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageWrapper>
      <div className="min-h-screen bg-gradient-to-b from-[#2D4A2A] to-[#1a2e18]">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
        </div>

        <div className="px-6 pb-8">
          <div className="max-w-md mx-auto">
            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <h1 className="font-heading text-3xl text-white mb-2">
                Record Your Affirmation
              </h1>
              <p className="text-white/60">
                Speak your truth in your own voice
              </p>
            </motion.div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`w-3 h-3 rounded-full transition-all ${
                    step >= s ? 'bg-white' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>

            <AnimatePresence mode="wait">
              {/* Step 1: Enter Text */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20">
                    <label className="block text-white/80 text-sm font-medium mb-3">
                      What would you like to say?
                    </label>
                    <textarea
                      value={affirmationText}
                      onChange={(e) => setAffirmationText(e.target.value)}
                      placeholder="I am calm, confident, and in control..."
                      className="w-full h-32 bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 resize-none"
                      data-testid="affirmation-text-input"
                    />
                    <p className="text-white/40 text-xs mt-2">
                      Tip: Keep it concise and present tense
                    </p>
                  </Card>

                  <Button
                    onClick={() => {
                      if (affirmationText.trim()) {
                        setStep(2);
                      } else {
                        toast.error("Please enter your affirmation text");
                      }
                    }}
                    className="w-full py-4"
                    disabled={!affirmationText.trim()}
                    data-testid="next-to-record-btn"
                  >
                    Next: Record
                    <ArrowLeft size={18} className="rotate-180 ml-2" />
                  </Button>
                </motion.div>
              )}

              {/* Step 2: Record */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Display the text to read */}
                  <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20">
                    <p className="text-white/60 text-sm mb-2">Read this aloud:</p>
                    <p className="text-white text-xl font-heading italic leading-relaxed">
                      "{affirmationText}"
                    </p>
                  </Card>

                  {/* Recording Button */}
                  <div className="flex flex-col items-center py-8">
                    <motion.button
                      onClick={isRecording ? stopRecording : startRecording}
                      whileTap={{ scale: 0.95 }}
                      className={`w-32 h-32 rounded-full flex items-center justify-center transition-all ${
                        isRecording 
                          ? 'bg-red-500 animate-pulse' 
                          : 'bg-white hover:bg-white/90'
                      }`}
                      data-testid="record-button"
                    >
                      {isRecording ? (
                        <Square size={40} className="text-white" />
                      ) : (
                        <Mic size={40} className="text-[#2D4A2A]" />
                      )}
                    </motion.button>

                    <p className="text-white/80 text-lg mt-4">
                      {isRecording ? formatTime(recordingTime) : "Tap to record"}
                    </p>

                    {isRecording && (
                      <p className="text-white/50 text-sm mt-2">
                        Tap to stop when finished
                      </p>
                    )}
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-400 bg-red-400/10 rounded-lg p-4">
                      <AlertCircle size={20} />
                      <p className="text-sm">{error}</p>
                    </div>
                  )}

                  <button
                    onClick={() => setStep(1)}
                    className="w-full text-white/50 hover:text-white/70 text-sm py-2"
                  >
                    ← Edit text
                  </button>
                </motion.div>
              )}

              {/* Step 3: Review */}
              {step === 3 && recordedUrl && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20">
                    <p className="text-white/60 text-sm mb-2">Your recording:</p>
                    <p className="text-white text-lg font-heading italic mb-6">
                      "{affirmationText}"
                    </p>

                    {/* Hidden audio element */}
                    <audio
                      ref={audioRef}
                      src={recordedUrl}
                      onEnded={handleAudioEnded}
                    />

                    {/* Playback Controls */}
                    <div className="flex items-center justify-center gap-4">
                      <button
                        onClick={togglePlayback}
                        className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:bg-white/90 transition-colors"
                        data-testid="play-recording-btn"
                      >
                        {isPlaying ? (
                          <Pause size={28} className="text-[#2D4A2A]" />
                        ) : (
                          <Play size={28} className="text-[#2D4A2A] ml-1" />
                        )}
                      </button>
                    </div>

                    <p className="text-white/40 text-center text-sm mt-4">
                      Duration: {formatTime(recordingTime)}
                    </p>
                  </Card>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button
                      onClick={saveRecording}
                      disabled={saving}
                      className="w-full py-4"
                      data-testid="save-recording-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 size={20} className="animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check size={20} className="mr-2" />
                          Save Recording
                        </>
                      )}
                    </Button>

                    <button
                      onClick={reRecord}
                      className="w-full flex items-center justify-center gap-2 text-white/60 hover:text-white py-3 transition-colors"
                    >
                      <RotateCcw size={18} />
                      Record Again
                    </button>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-400 bg-red-400/10 rounded-lg p-4">
                      <AlertCircle size={20} />
                      <p className="text-sm">{error}</p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Step 4: Success */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                    className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
                  >
                    <Check size={40} className="text-white" />
                  </motion.div>
                  <h2 className="font-heading text-2xl text-white mb-2">
                    Recording Saved!
                  </h2>
                  <p className="text-white/60">
                    Your affirmation is ready to use
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default RecordAffirmationPage;
